import { useState } from "react";
import { Circle, Play, Square, Wrench } from "lucide-react";
import { MmcFrame } from "../../admin-framework/MmcFrame.js";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const serviceRows = [
  { name: "TermService", displayName: "Remote Access Services", status: "Running", startupType: "Automatic", account: "Network Service" },
  { name: "Spooler", displayName: "Print Spooler", status: "Stopped", startupType: "Manual", account: "Local Service" },
  { name: "WinRM", displayName: "Remote Management", status: "Running", startupType: "Automatic", account: "Local System" },
  { name: "Dnscache", displayName: "DNS Client", status: "Running", startupType: "Automatic", account: "Network Service" },
  { name: "RemoteRegistry", displayName: "Remote Registry", status: "Stopped", startupType: "Disabled", account: "Local Service" }
];

export function ServicesApp({ activeMachine, onRunCommand }: SimulatorAppSurfaceProps) {
  const [serviceName, setServiceName] = useState("TermService");
  const [startupType, setStartupType] = useState("automatic");

  async function runServiceCommand(
    commandType: "SERVICE_SET_STARTUP_TYPE" | "SERVICE_START" | "SERVICE_STOP",
    nextServiceName = serviceName
  ) {
    await onRunCommand({
      appId: "services",
      commandType,
      capability: "service.write",
      label: `${commandType} ${nextServiceName}`,
      payload: {
        machineId: activeMachine,
        serviceName: nextServiceName,
        startupType
      }
    });
  }

  return (
    <div className="fd-sim-app fd-sim-mmc">
      <MmcFrame
        actions={[
          { label: "Start", onClick: () => void runServiceCommand("SERVICE_START") },
          { label: "Stop", onClick: () => void runServiceCommand("SERVICE_STOP") },
          { label: "Startup", onClick: () => void runServiceCommand("SERVICE_SET_STARTUP_TYPE") }
        ]}
        columns={[
          { key: "state", label: "" },
          { key: "name", label: "Name" },
          { key: "description", label: "Description" },
          { key: "status", label: "Status" },
          { key: "startupType", label: "Startup type" },
          { key: "account", label: "Log on as" }
        ]}
        rows={serviceRows.map((service) => ({
          id: service.name,
          onDoubleClick: () => {
            setServiceName(service.name);
            void runServiceCommand(service.status === "Running" ? "SERVICE_SET_STARTUP_TYPE" : "SERVICE_START", service.name);
          },
          values: {
            account: service.account,
            description: service.displayName,
            name: <button className="fd-mmc-link" type="button" onClick={() => setServiceName(service.name)}>{service.name}</button>,
            startupType: service.startupType,
            state: service.status === "Running" ? <Circle className="fd-mmc-dot is-on" size={10} /> : <Circle className="fd-mmc-dot" size={10} />,
            status: service.status
          }
        }))}
        title="Services"
        tree={[
          { active: true, icon: <Wrench size={15} />, id: "local", label: `Services (${activeMachine})` },
          { id: "extended", label: "Extended View" },
          { id: "standard", label: "Standard View" }
        ]}
      >
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Selected service</p>
              <h3>{serviceName}</h3>
            </div>
            <div className="fd-mmc-inline-actions">
              <button type="button" onClick={() => void runServiceCommand("SERVICE_START")}><Play size={14} /> Start</button>
              <button type="button" onClick={() => void runServiceCommand("SERVICE_STOP")}><Square size={14} /> Stop</button>
            </div>
          </header>
          <div className="fd-command-form">
            <label>
              Service name
              <input value={serviceName} onChange={(event) => setServiceName(event.target.value)} />
            </label>
            <label>
              Startup type
              <select value={startupType} onChange={(event) => setStartupType(event.target.value)}>
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <button type="button" onClick={() => void runServiceCommand("SERVICE_SET_STARTUP_TYPE")}>
              Apply startup
            </button>
          </div>
        </section>
      </MmcFrame>
    </div>
  );
}
